import { useState } from "react";
import { Link } from "react-router-dom";
import { ImageOff } from "lucide-react";
import { galleryImages } from "@/data/gallery";
import { useLang } from "@/lib/i18n";
import { breadcrumbSchema } from "@/lib/schema";
import { Seo } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { OrderButton } from "@/components/TrackedLinks";
import { GalleryFilters, GalleryGrid, Lightbox } from "@/components/gallery/GalleryParts";

export const GalleryPage = () => {
  const { t } = useLang();
  const [filter, setFilter] = useState("all");
  const [index, setIndex] = useState(null);

  const images = filter === "all" ? galleryImages : galleryImages.filter((i) => i.category === filter);
  const current = index != null ? images[index] : null;
  const step = (d) => setIndex((i) => (i + d + images.length) % images.length);
  const changeFilter = (c) => { setFilter(c); setIndex(null); };

  return (
    <div data-testid="gallery-page">
      <Seo
        title="Food Gallery | Tijuas Tacos Birrieria Albuquerque"
        description="Real photos of quesabirria tacos, birria pizza, birria ramen, birria fries and more from Tijuas Tacos Birrieria in Albuquerque."
        path="/gallery"
        jsonLd={breadcrumbSchema([{ label: t("nav.gallery"), to: "/gallery" }])}
      />
      <Breadcrumbs items={[{ label: t("nav.gallery"), to: "/gallery" }]} />

      <section className="py-16 px-4" data-testid="gallery-hero">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-hacienda-terracotta text-sm uppercase tracking-[0.2em] mb-4">{t("gallery.eyebrow")}</p>
          <h1 className="font-serif text-5xl md:text-6xl text-hacienda-charcoal font-semibold mb-5">{t("gallery.title")}</h1>
          <p className="text-hacienda-gray text-lg">{t("gallery.sub")}</p>
        </div>
      </section>

      <section className="pb-24 px-4" data-testid="gallery-grid-section">
        <div className="max-w-7xl mx-auto">
          <GalleryFilters filter={filter} onChange={changeFilter} />
          {images.length === 0 ? (
            <div className="text-center py-20 text-hacienda-gray" data-testid="gallery-empty">
              <ImageOff size={40} className="mx-auto mb-4 text-hacienda-border-dark" aria-hidden="true" />
              <p className="text-lg">{t("gallery.empty")}</p>
            </div>
          ) : (
            <GalleryGrid images={images} onSelect={setIndex} />
          )}
          <div className="text-center mt-14 flex flex-col sm:flex-row gap-4 justify-center">
            <OrderButton location="gallery" size="lg" data-testid="gallery-order-btn" />
            <Link to="/menu" className="inline-flex items-center justify-center rounded-full border-2 border-hacienda-terracotta text-hacienda-terracotta hover:bg-hacienda-terracotta hover:text-white px-9 py-4 text-lg font-medium transition-colors" data-testid="gallery-view-menu-btn">{t("actions.viewMenu")}</Link>
          </div>
        </div>
      </section>

      <Lightbox image={current} onClose={() => setIndex(null)} onPrev={() => step(-1)} onNext={() => step(1)} />
    </div>
  );
};

export default GalleryPage;
